import React, { useState, useEffect } from "react"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import LoadingScreen from "components/common/LoadingScreen"
import { verifyResponse, httpGet } from "utils/httpRequest"
import { StyledMcpAuditLog } from "./McpAuditLog.style"

const AVAILABLE_TOOLS = [
  { value: "manage_programs", label: "Manage Programs" },
  { value: "manage_lessons", label: "Manage Lessons" },
  { value: "manage_events", label: "Manage Events" },
  { value: "manage_ai_agents", label: "Manage AI Agents" },
  { value: "manage_ai_agents", label: "Manage AI Agents" },
  { value: "search_content", label: "Search Content" },
  { value: "get_content", label: "Get Content" },
  { value: "count_content", label: "Count Content" },
  { value: "aggregate_content", label: "Aggregate Content" },
  { value: "get_related_content", label: "Get Related Content" },
  { value: "get_dashboard_metrics", label: "Get Dashboard Metrics" },
  { value: "upload_resources", label: "Upload Resources" },
  { value: "upload_attachment", label: "Upload Attachment" },
  { value: "upload_from_url", label: "Upload From URL" },
  { value: "search_media", label: "Search Media" },
  { value: "start_create_draft", label: "Start Create Draft" },
  { value: "update_create_draft", label: "Update Create Draft" },
  { value: "get_create_draft", label: "Get Create Draft" },
  { value: "list_create_drafts", label: "List Create Drafts" },
  { value: "validate_create_draft", label: "Validate Create Draft" },
  { value: "submit_create_draft", label: "Submit Create Draft" },
  { value: "cancel_create_draft", label: "Cancel Create Draft" },
  { value: "get_reference_data", label: "Get Reference Data" },
  { value: "_auth", label: "Auth / Session" },
]

const STATUS_BADGE = {
  success: "Success",
  denied: "Denied",
  failed: "Failed",
  partial: "Partial",
}

const STATUS_CLASS = {
  success: "success",
  denied: "denied",
  failed: "failed",
  partial: "partial",
}

const EMPTY_FILTERS = {
  mcpUserId: "",
  toolName: "",
  status: "",
}

const parseAuditLogs = (response) => {
  const payload = response?.data
  if (!payload) return { logs: [], total: 0 }

  const inner = payload.data
  if (Array.isArray(inner)) {
    return { logs: inner, total: inner.length }
  }
  if (inner && Array.isArray(inner.data)) {
    return { logs: inner.data, total: inner.total ?? inner.data.length }
  }
  return { logs: [], total: 0 }
}

const McpAuditLog = ({ embedded = false }) => {
  const [logs, setLogs] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  const [filters, setFilters] = useState(EMPTY_FILTERS)

  const loadAuditLogs = async (activeFilters = filters) => {
    setLoading(true)
    try {
      let url = "admin/ai-platform/audit/query"
      const params = new URLSearchParams()

      if (activeFilters.mcpUserId) params.append("mcpUserId", activeFilters.mcpUserId)
      if (activeFilters.toolName) params.append("toolName", activeFilters.toolName)
      if (activeFilters.status) params.append("status", activeFilters.status)
      params.append("limit", "200")

      if (params.toString()) {
        url += "?" + params.toString()
      }

      const response = await httpGet(url)
      verifyResponse(response.data)
      const { logs: auditLogs, total: count } = parseAuditLogs(response)
      setLogs(auditLogs)
      setTotal(count)
    } catch (error) {
      console.error("Failed to load audit logs:", error)
      setLogs([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAuditLogs(EMPTY_FILTERS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formatDate = (date) => {
    if (!date) return "-"
    return new Date(date).toLocaleString()
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleApplyFilters = () => {
    loadAuditLogs(filters)
  }

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS)
    loadAuditLogs(EMPTY_FILTERS)
  }

  const toggleExpanded = (logId) => {
    setExpandedId((current) => (current === logId ? null : logId))
  }

  if (loading && logs.length === 0) {
    return <LoadingScreen />
  }

  return (
    <StyledMcpAuditLog>
      {!embedded && <h2>MCP Audit Log</h2>}

      <div className="filters-section">
        <h3>Filters</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "15px" }}>
          <TextField
            label="MCP User ID/Email"
            value={filters.mcpUserId}
            onChange={(e) => handleFilterChange("mcpUserId", e.target.value)}
            placeholder="e.g., devops@plasmacomp.com"
            size="small"
            fullWidth
          />

          <FormControl fullWidth size="small">
            <InputLabel>Tool Name</InputLabel>
            <Select
              value={filters.toolName}
              onChange={(e) => handleFilterChange("toolName", e.target.value)}
              label="Tool Name"
            >
              <MenuItem value="">All Tools</MenuItem>
              {AVAILABLE_TOOLS.map((tool) => (
                <MenuItem key={tool.value} value={tool.value}>
                  {tool.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="denied">Denied</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
            </Select>
          </FormControl>

          <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
              size="small"
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              size="small"
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              onClick={() => loadAuditLogs(filters)}
              size="small"
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="results-summary">
        Showing {logs.length} of {total} request{total === 1 ? "" : "s"}
      </div>

      <div className="audit-table-section">
        {logs.length === 0 ? (
          <div className="empty-state">
            <p>No audit logs found</p>
            <p>
              MCP tool calls and auth failures will appear here after the backend is deployed with audit logging enabled.
            </p>
          </div>
        ) : (
          <table className="audit-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Tool</th>
                <th>Operation</th>
                <th>Status</th>
                <th>Error</th>
                <th>Entity</th>
                <th>Time (ms)</th>
                <th>Timestamp</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const statusClass = STATUS_CLASS[log.status] || "failed"
                const isExpanded = expandedId === log.id

                return (
                  <React.Fragment key={log.id}>
                    <tr className={log.status === "denied" ? "row-denied" : undefined}>
                      <td className="cell-user">
                        <strong>{log.mcpUserEmail || log.mcpUserId || "-"}</strong>
                      </td>
                      <td>{log.toolName}</td>
                      <td>
                        <code className="operation-code">{log.operation || "-"}</code>
                      </td>
                      <td>
                        <span className={`status-badge ${statusClass}`}>
                          {STATUS_BADGE[log.status] || log.status}
                        </span>
                      </td>
                      <td className="cell-error">
                        {log.errorMessage || "-"}
                      </td>
                      <td>
                        {log.entityType ? (
                          <>
                            <strong>{log.entityType}</strong>
                            {log.entityId && ` #${log.entityId}`}
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {log.executionTimeMs ?? "-"}
                      </td>
                      <td className="cell-timestamp">
                        {formatDate(log.createdAt)}
                      </td>
                      <td>
                        <Button size="small" onClick={() => toggleExpanded(log.id)}>
                          {isExpanded ? "Hide" : "Details"}
                        </Button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="details-row">
                        <td colSpan={9} style={{ padding: "12px 16px" }}>
                          <div className="details-panel">
                            {log.requestId && (
                              <div><strong>Request ID:</strong> {log.requestId}</div>
                            )}
                            {log.sessionId && (
                              <div><strong>Session ID:</strong> {log.sessionId}</div>
                            )}
                            {log.toolInput && (
                              <div>
                                <strong>Input:</strong>
                                <pre>{JSON.stringify(log.toolInput, null, 2)}</pre>
                              </div>
                            )}
                            {log.result && (
                              <div>
                                <strong>Result:</strong>
                                <pre>{JSON.stringify(log.result, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </StyledMcpAuditLog>
  )
}

export default McpAuditLog
